/**
 * Folder TanStack Mutation hooks.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { folderKeys } from "@/queries/keys";
import {
  createFolder,
  updateFolder,
  deleteFolder,
  cloneFolder,
  reanalyzeFolder,
  createFolderView,
  updateFolderView,
  deleteFolderView,
  updateShowOrder,
  setupFolderAutomation,
} from "./api";
import type {
  CreateFolderData,
  UpdateFolderData,
  CloneFolderData,
  ReanalyzeFolderParams,
  CreateFolderViewData,
} from "./api";

// -- Mutation Hooks --------------------------------------------------

/** Create a new folder. */
export function useCreateFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateFolderData) => {
      const res = await createFolder(params);
      if (!res.success)
        throw new Error(res.error ?? "Failed to create folder");
      return res.data!;
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
}

interface UpdateFolderParams extends UpdateFolderData {
  folderId: string;
}

/** Update an existing folder. */
export function useUpdateFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateFolderParams) => {
      const { folderId, ...data } = params;
      const res = await updateFolder(folderId, data);
      if (!res.success)
        throw new Error(res.error ?? "Failed to update folder");
      return res.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: folderKeys.detail(variables.folderId),
      });
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
    },
  });
}

/** Delete a folder. */
export function useDeleteFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (folderId: string) => {
      const res = await deleteFolder(folderId);
      if (!res.success)
        throw new Error(res.error ?? "Failed to delete folder");
      return res.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
}

/** Clone a folder. */
export function useCloneFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CloneFolderData) => {
      const res = await cloneFolder(params);
      if (!res.success) throw new Error(res.error ?? "Failed to clone folder");
      return res.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
}

export type { ReanalyzeFolderParams };

/**
 * Re-analyze all media in a folder. Angular: FolderService → POST /folder/reanalyze.
 *
 * Accepts a folderId string for the "reanalyze everything" callsite (all four signal
 * flags default to true so the action actually does something), or a full param object
 * when a future UI needs per-signal control.
 */
export function useReanalyzeFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: string | ReanalyzeFolderParams) => {
      const params: ReanalyzeFolderParams =
        typeof input === "string"
          ? {
              folderId: input,
              isInsights: true,
              isSentiment: true,
              isFillerWords: true,
              isEmbeddings: true,
            }
          : input;
      const res = await reanalyzeFolder(params);
      if (!res.success) throw new Error(res.error ?? "Failed to re-analyze folder");
      return res.data!;
    },
    onSuccess: () => {
      // Re-analysis queues background jobs — invalidate so stale insight counts
      // or status badges refresh when the user next navigates to the folder.
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
}

// -- View Mutation Types -------------------------------------------

interface CreateFolderViewParams extends CreateFolderViewData {
  folderId: string;
}

interface UpdateFolderViewParams extends CreateFolderViewParams {
  viewId: string;
}

interface DeleteFolderViewParams {
  folderId: string;
  viewId: string;
}

/** Create a new saved view for a folder. */
export function useCreateFolderView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ folderId, ...data }: CreateFolderViewParams) => {
      const res = await createFolderView(folderId, data);
      if (!res.success) throw new Error(res.error ?? "Failed to create view");
      return res.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: folderKeys.views(variables.folderId) });
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
    },
  });
}

/** Update an existing saved view. */
export function useUpdateFolderView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ folderId, viewId, ...data }: UpdateFolderViewParams) => {
      const res = await updateFolderView(folderId, viewId, data);
      if (!res.success) throw new Error(res.error ?? "Failed to update view");
      return res.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: folderKeys.views(variables.folderId) });
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
    },
  });
}

/** Delete a saved view. */
export function useDeleteFolderView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ folderId, viewId }: DeleteFolderViewParams) => {
      const res = await deleteFolderView(folderId, viewId);
      if (!res.success) throw new Error(res.error ?? "Failed to delete view");
      return res.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: folderKeys.views(variables.folderId) });
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
    },
  });
}

interface UpdateShowOrderParams {
  folderId: string;
  prevIndex: number;
  currentIndex: number;
}

/** Update drag-and-drop sort order. Angular: FolderService.updateShowOrder() → PUT /folder/showOrder/:folderId */
export function useUpdateShowOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ folderId, prevIndex, currentIndex }: UpdateShowOrderParams) => {
      const res = await updateShowOrder(folderId, prevIndex, currentIndex);
      if (!res.success) throw new Error(res.error ?? "Failed to update folder order");
      return res.data!;
    },
    onSuccess: () => {
      // Invalidate folder lists only (not detail queries)
      queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
    },
  });
}

interface SetupFolderAutomationParams {
  folderId: string;
  fields: Array<{ fieldId: string }>;
}

/**
 * Configure field-level AI automation for a folder.
 * Angular: FolderService.fieldsAutomation() → PUT /folder/automation/:folderId
 *
 * Server Joi `folderFieldsAutomation` only accepts `{ fields: [{ fieldId }] }` (max 10).
 * Any additional keys (e.g. `promptText`) are rejected with 400.
 */
export function useSetupFolderAutomation() {
  return useMutation({
    mutationFn: async ({ folderId, fields }: SetupFolderAutomationParams) => {
      const res = await setupFolderAutomation(folderId, fields);
      if (!res.success) throw new Error(res.error ?? "Failed to set up automation");
      return res.data!;
    },
  });
}
